import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Table,
  Spinner,
  Badge
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faTimes,
  faDownload,
  faPlus,
  faFileInvoice
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import apiClient from 'api/client';
import { getTenantFromToken } from 'utils/routeUtils';

// ── API calls ────────────────────────────────────────────────────────────────
const PO_REPORT_URL = '/digitization/v1/purchase-order/report';

const fetchPOReport = params => apiClient.post(PO_REPORT_URL, params);

// ── helpers ──────────────────────────────────────────────────────────────────
const fmt = n => Number(n || 0).toLocaleString();
const fmtUGX = n => `${Math.round(Number(n) || 0).toLocaleString()} UGX`;

const STATUS_COLOURS = {
  draft: 'secondary',
  submitted: 'primary',
  approved: 'success',
  rejected: 'danger',
  completed: 'success'
};

const getStatusBadge = status =>
  STATUS_COLOURS[status?.toLowerCase()] || 'primary';

// ── component ─────────────────────────────────────────────────────────────────
export default function PurchaseOrderReport() {
  const navigate = useNavigate();
  const tenant = getTenantFromToken();

  // ── filter state ─────────────────────────────────────────────────────────
  const [searchText, setSearchText] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // ── table data ────────────────────────────────────────────────────────────
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  // ── load master data on mount ─────────────────────────────────────────────
  useEffect(() => {
    handleSearch();
  }, []);

  // ── run report ────────────────────────────────────────────────────────────
  const handleSearch = useCallback(async () => {
    setLoading(true);
    setSearched(true);
    try {
      const res = await fetchPOReport({
        cursor: 0,
        limit: 1000,
        filters: {
          search_text: searchText || '',
          start_date: startDate || '',
          end_date: endDate || ''
        }
      });
      setRows(res.data?.results?.data || []);
    } catch (err) {
      toast.error('Failed to load purchase order report');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [searchText, startDate, endDate]);

  // ── clear filters ─────────────────────────────────────────────────────────
  const handleClear = async () => {
    setSearchText('');
    setStartDate('');
    setEndDate('');

    setLoading(true);
    setSearched(true);
    try {
      const res = await fetchPOReport({
        cursor: 0,
        limit: 1000,
        filters: {
          search_text: '',
          start_date: '',
          end_date: ''
        }
      });
      setRows(res.data?.results?.data || []);
    } catch (err) {
      toast.error('Failed to load purchase order report');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  // ── CSV export ────────────────────────────────────────────────────────────
  const handleExport = () => {
    if (!rows.length) {
      toast.warning('No data to export');
      return;
    }
    const headers = Object.keys(rows[0]).filter(
      k => k !== 'id' && k !== 'action'
    );
    const csv = [
      headers.join(','),
      ...rows.map(r => headers.map(h => `"${r[h] ?? ''}"`).join(','))
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `PurchaseOrderReport_${new Date()
      .toISOString()
      .substring(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── totals ────────────────────────────────────────────────────────────────
  const totalValue = rows.reduce(
    (s, r) => s + Number(r['Total Value (UGX)'] || 0),
    0
  );

  // ── style constants ───────────────────────────────────────────────────────
  const inputStyle = {
    borderRadius: '8px',
    padding: '8px 12px',
    border: '1.5px solid #D8E0F0',
    fontSize: '13px'
  };

  const COLUMNS = [
    'PO Code',
    'PO Date',
    'Supplier',
    'Warehouse',
    'Delivery Date',
    'Payment Mode',
    'Status',
    'Total Items',
    'Total Qty',
    'Total Value (UGX)'
  ];

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Container
      fluid
      className="py-4 px-4"
      style={{ backgroundColor: '#F4F6FB', minHeight: '100vh' }}
    >
      {/* ── page header ── */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <h4 className="mb-1 fw-bold text-dark" style={{ fontSize: '22px' }}>
            Purchase Orders
          </h4>
          <div className="text-muted" style={{ fontSize: '12px' }}>
            Inventory &gt; Purchase Orders
          </div>
        </div>
        <div className="d-flex gap-2">
          <Button
            variant="primary"
            className="rounded-pill d-flex align-items-center gap-2 shadow-sm"
            style={{ fontSize: '13px', padding: '7px 18px' }}
            onClick={() => navigate(`/${tenant}/create-purchase-order`)}
          >
            <FontAwesomeIcon icon={faPlus} /> Create Purchase Order
          </Button>
          <Button
            variant="outline-success"
            className="rounded-pill d-flex align-items-center gap-2"
            style={{ fontSize: '13px', padding: '7px 18px' }}
            onClick={handleExport}
            disabled={!rows.length}
          >
            <FontAwesomeIcon icon={faDownload} /> Export CSV
          </Button>
        </div>
      </div>

      {/* ── FILTER CARD ── */}
      <Card
        className="shadow-sm border-0 mb-4"
        style={{ borderRadius: '14px' }}
      >
        <Card.Body className="p-4">
          <h6 className="fw-bold text-dark mb-3" style={{ fontSize: '14px' }}>
            Filter / Search
          </h6>
          <Row className="g-3 align-items-end">
            <Col md={3} sm={6}>
              <Form.Label
                className="fw-semibold text-secondary mb-1"
                style={{ fontSize: '12px' }}
              >
                Search Code, Supplier, Warehouse
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Search..."
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                style={inputStyle}
              />
            </Col>
            <Col md={2} sm={6}>
              <Form.Label
                className="fw-semibold text-secondary mb-1"
                style={{ fontSize: '12px' }}
              >
                Start Date
              </Form.Label>
              <Form.Control
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                style={inputStyle}
              />
            </Col>
            <Col md={2} sm={6}>
              <Form.Label
                className="fw-semibold text-secondary mb-1"
                style={{ fontSize: '12px' }}
              >
                End Date
              </Form.Label>
              <Form.Control
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                style={inputStyle}
              />
            </Col>
            <Col md={4} sm={12}>
              <div className="d-flex gap-2">
                <Button
                  variant="primary"
                  className="rounded-pill d-flex align-items-center gap-2 px-4"
                  style={{ fontSize: '13px', padding: '9px 20px' }}
                  onClick={handleSearch}
                  disabled={loading}
                >
                  {loading ? (
                    <Spinner size="sm" animation="border" />
                  ) : (
                    <FontAwesomeIcon icon={faSearch} />
                  )}
                  {loading ? 'Loading…' : 'Search'}
                </Button>
                <Button
                  variant="outline-secondary"
                  className="rounded-pill d-flex align-items-center gap-2"
                  style={{ fontSize: '13px', padding: '9px 16px' }}
                  onClick={handleClear}
                >
                  <FontAwesomeIcon icon={faTimes} /> Clear
                </Button>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* ── SUMMARY TILES ── */}
      {searched && !loading && rows.length > 0 && (
        <Row className="g-3 mb-4">
          <Col md={3} sm={6} xs={12}>
            <Card
              className="border-0 shadow-sm h-100"
              style={{ borderRadius: '12px', backgroundColor: '#EEF2FF' }}
            >
              <Card.Body className="p-3">
                <div
                  className="text-muted mb-1"
                  style={{
                    fontSize: '11px',
                    fontWeight: 500,
                    textTransform: 'uppercase'
                  }}
                >
                  Total Purchase Orders
                </div>
                <div
                  className="fw-bold"
                  style={{ fontSize: '18px', color: '#6366F1' }}
                >
                  {fmt(rows.length)}
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3} sm={6} xs={12}>
            <Card
              className="border-0 shadow-sm h-100"
              style={{ borderRadius: '12px', backgroundColor: '#F0F9FF' }}
            >
              <Card.Body className="p-3">
                <div
                  className="text-muted mb-1"
                  style={{
                    fontSize: '11px',
                    fontWeight: 500,
                    textTransform: 'uppercase'
                  }}
                >
                  Total Value (UGX)
                </div>
                <div
                  className="fw-bold"
                  style={{ fontSize: '18px', color: '#0EA5E9' }}
                >
                  {fmtUGX(totalValue)}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* ── TABLE CARD ── */}
      <Card className="shadow-sm border-0" style={{ borderRadius: '14px' }}>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">Loading purchase orders…</p>
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <FontAwesomeIcon
                icon={faFileInvoice}
                size="3x"
                className="mb-3"
                style={{ opacity: 0.35 }}
              />
              <p style={{ fontSize: '15px' }}>No purchase orders found</p>
            </div>
          ) : (
            <div
              className="table-responsive"
              style={{ maxHeight: '65vh', overflowY: 'auto' }}
            >
              <Table
                hover
                className="align-middle mb-0"
                style={{ fontSize: '12.5px' }}
              >
                <thead
                  style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    backgroundColor: '#F8FAFC'
                  }}
                >
                  <tr>
                    <th
                      className="text-secondary fw-semibold py-3 ps-4"
                      style={{ minWidth: '40px' }}
                    >
                      #
                    </th>
                    {COLUMNS.map(col => (
                      <th
                        key={col}
                        className="text-secondary fw-semibold py-3"
                        style={{ minWidth: '120px', whiteSpace: 'nowrap' }}
                      >
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #F8FAFC' }}>
                      <td className="ps-4 text-secondary">{idx + 1}</td>
                      <td className="fw-bold text-primary">
                        {row['PO Code'] || '—'}
                      </td>
                      <td>{row['PO Date'] || '—'}</td>
                      <td>
                        <div className="fw-semibold text-dark">
                          {row['Supplier'] || '—'}
                        </div>
                      </td>
                      <td>{row['Warehouse'] || '—'}</td>
                      <td>{row['Delivery Date'] || '—'}</td>
                      <td>{row['Payment Mode'] || '—'}</td>
                      <td>
                        <Badge
                          bg={getStatusBadge(row['Status'])}
                          className="px-2 py-1"
                          style={{
                            fontSize: '11px',
                            textTransform: 'capitalize'
                          }}
                        >
                          {row['Status'] || 'draft'}
                        </Badge>
                      </td>
                      <td className="text-end">{fmt(row['Total Items'])}</td>
                      <td className="text-end">{fmt(row['Total Qty'])}</td>
                      <td className="text-end fw-bold text-dark pe-4">
                        {fmtUGX(row['Total Value (UGX)'])}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}
